import { Link, NavLink } from "react-router-dom";
import Icon from "@/components/ui/Icon.jsx";
import { FOOTER_NAV, navGroupsFor } from "@/lib/nav.js";
import { useAuth } from "@/context/auth.js";

/*
 * Ported from html-markup/assets/js/shell.js sidebarHtml().
 *
 * Two responsive modes, exactly as the markup:
 *   ≥ lg   sticky rail. `collapsed` swaps w-64 → lg:w-18 and hides every
 *          [data-collapse-hide] element via the .sidebar-collapsed rules in
 *          index.css.
 *   < lg   off-canvas drawer, translated out by default, with a scrim.
 *
 * The width swap has to be a utility class rather than something in the
 * components layer — a utility always outranks @layer components, so
 * lg:w-18 would otherwise lose to the base w-64.
 */
export default function Sidebar({ collapsed, drawerOpen, onCloseDrawer, role, userCount }) {
    const { logout } = useAuth();

    const badgeValue = (name) => (name === "userCount" ? userCount : null);

    return (
        <>
            <aside
                className={[
                    "fixed inset-y-0 left-0 z-40 flex w-64 shrink-0 flex-col border-r border-slate-200 bg-white",
                    "transition-[width,transform] duration-200 lg:sticky lg:top-0 lg:h-screen lg:translate-x-0",
                    "dark:border-slate-800 dark:bg-slate-900",
                    drawerOpen ? "translate-x-0" : "-translate-x-full",
                    collapsed ? "sidebar-collapsed lg:w-18" : "",
                ].join(" ")}
            >
                {/* Brand */}
                <Link
                    to="/dashboard"
                    className="flex h-15 shrink-0 items-center gap-2.5 border-b border-slate-200 px-4.5 dark:border-slate-800"
                >
                    <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-brand-600 to-violet-500 text-white">
                        <Icon name="clock" className="size-4.5" />
                    </span>
                    <span
                        data-collapse-hide
                        className="truncate text-[15px] font-semibold tracking-tight"
                    >
                        Performance Track
                    </span>
                </Link>

                {/* Groups */}
                <nav className="flex-1 overflow-x-hidden overflow-y-auto px-3 py-3.5">
                    {navGroupsFor(role).map((group) => (
                        <div key={group.key} className="mt-5 first:mt-0">
                            <p
                                data-collapse-hide
                                className="mb-2 flex items-baseline gap-1.5 px-2.5 text-[11px] font-semibold tracking-widest text-slate-400 uppercase dark:text-slate-500"
                            >
                                {group.label}
                                {group.note && (
                                    <span className="text-[9.5px] font-normal tracking-normal normal-case opacity-70">
                                        {group.note}
                                    </span>
                                )}
                            </p>

                            <div className="space-y-0.5">
                                {group.items.map((item) => (
                                    <NavItem
                                        key={item.key}
                                        item={item}
                                        badge={badgeValue(item.badge)}
                                        onNavigate={onCloseDrawer}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </nav>

                {/* Footer */}
                <div className="shrink-0 space-y-0.5 border-t border-slate-200 p-3 dark:border-slate-800">
                    {/* A footer item may carry `roles` (e.g. Download App is org-only,
                        so a superAdmin — who has no organization — never sees it). */}
                    {FOOTER_NAV.filter((item) => !item.roles || item.roles.includes(role)).map((item) => (
                        <NavItem key={item.key} item={item} onNavigate={onCloseDrawer} />
                    ))}

                    {/* A button, not a link: signing out has to clear the session
                        and the query cache, not just navigate. */}
                    <button
                        type="button"
                        onClick={logout}
                        className="nav-item w-full text-rose-600 hover:bg-rose-50 hover:text-rose-700 dark:text-rose-400 dark:hover:bg-rose-950/40 dark:hover:text-rose-300"
                    >
                        <Icon name="logout" />
                        <span data-collapse-hide>Sign Out</span>
                    </button>
                </div>
            </aside>

            {/* Mobile scrim */}
            {drawerOpen && (
                <div
                    onClick={onCloseDrawer}
                    className="fixed inset-0 z-30 bg-slate-900/50 lg:hidden"
                    aria-hidden="true"
                />
            )}
        </>
    );
}

function NavItem({ item, badge, onNavigate }) {
    return (
        <NavLink
            to={item.to}
            onClick={onNavigate}
            className={({ isActive }) => `nav-item${isActive ? " nav-item-active" : ""}`}
        >
            {({ isActive }) => (
                <>
                    <Icon name={item.icon} />
                    <span data-collapse-hide>{item.label}</span>
                    {badge != null && (
                        <span
                            data-collapse-hide
                            className={`ml-auto rounded-full px-1.5 py-px text-[11px] font-semibold ${
                                isActive
                                    ? "bg-brand-100 text-brand-700 dark:bg-brand-900 dark:text-brand-200"
                                    : "bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-200"
                            }`}
                        >
                            {badge}
                        </span>
                    )}
                </>
            )}
        </NavLink>
    );
}
